import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { delIcon, editIcon } from "assets/icons";
import Button from "../../components/Buttons/Button";
import IconButton from "../../components/Buttons/IconButton";
import { FileDisplay } from "../../components/FileToDisplay/FileToDisplay.types";
import { ConfirmModal, CreateModal } from "../../components/Modals";
import Sidebar from "../../components/Sidebar";
import Table, { TableText } from "../../components/Table";
import { useNotify } from "../../context/NotificationContext";
import { useListPage } from "../../hooks/useListPage";
import {
  createLesson,
  deleteLesson,
  fetchLessonCategories,
  fetchLessons,
  updateLesson,
} from "../../services/lessonService/lessonService";
import type { LessonData } from "../../services/lessonService/lessonService.types";
import {
  fetchKnowledgeBaseCourse,
} from "../../services/knowledgeBaseService/knowledgeBaseService";
import type { KnowledgeBaseCourse } from "../../services/knowledgeBaseService/knowledgeBaseService.types";
import { formatServerErrors } from "../../utils/errorHandler";
import { getServerErrorData } from "../../utils/httpError";
import { logger } from "../../utils/logger";
import type { Lesson } from "../LessonsPage/LessonsPage.types";
import {
  ButtonWrapper,
  ButtonsContainer,
  ContentWrapper,
  HeaderContainer,
  LessonsPageContainer,
  Title,
} from "../LessonsPage/LessonsPage.styles";
import { CompactLink } from "./KnowledgeBasePage.styles";

const KnowledgeBaseManagePage: React.FC = () => {
  const { courseId } = useParams();
  const courseIdNumber = Number(courseId);
  const [course, setCourse] = useState<KnowledgeBaseCourse | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLesson, setEditingLesson] = useState<Lesson | null>(null);
  const [deletingLesson, setDeletingLesson] = useState<Lesson | null>(null);
  const [storageFiles, setStorageFiles] = useState<FileDisplay[]>([]);
  const [externalErrors, setExternalErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const toast = useNotify();

  const {
    items: lessons,
    loading,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    handleSort,
    reload: loadLessons,
  } = useListPage<Lesson, Awaited<ReturnType<typeof fetchLessons>>>({
    fetcher: ({ page, pageSize: size, orderBy }) => fetchLessons(courseIdNumber, page, size, orderBy),
    select: (response) => ({ items: response.results, total: response.count }),
    errorMessage: "Ошибка загрузки материалов Базы знаний",
    initialPageSize: 6,
  });

  useEffect(() => {
    if (!courseIdNumber) return;
    fetchKnowledgeBaseCourse(courseIdNumber)
      .then((data) => {
        setCourse(data);
        if (!data.can_manage) {
          toast.show({
            severity: "error",
            summary: "Нет доступа",
            detail: "У этого аккаунта нет права редактировать данный материал",
            life: 3500,
          });
        }
      })
      .catch((error) => logger.error("Ошибка загрузки материала Базы знаний:", error));
    fetchLessonCategories(courseIdNumber)
      .then(setCategories)
      .catch((error) => logger.error("Ошибка загрузки категорий материалов:", error));
  }, [courseIdNumber, toast]);

  const openCreate = () => {
    setEditingLesson(null);
    setStorageFiles([]);
    setExternalErrors({});
    setModalOpen(true);
  };

  const openEdit = (lesson: Lesson) => {
    setEditingLesson(lesson);
    setStorageFiles(lesson.files_from_storage || []);
    setExternalErrors({});
    setModalOpen(true);
  };

  const handleSave = async (formData: Record<string, string | File[] | null | FileDisplay[]>) => {
    setSaving(true);
    setExternalErrors({});
    try {
      const uploadedFiles = Array.isArray(formData.files) ? (formData.files as File[]) : [];
      const lessonData: LessonData = {
        course: courseIdNumber,
        order: Number(formData.order),
        title: String(formData.title || "").trim(),
        category_title: String(formData.category || "").trim(),
        content: String(formData.content || ""),
        video_url: String(formData.video_url || ""),
        files: uploadedFiles,
        files_from_storage: storageFiles,
      };

      if (editingLesson) {
        await updateLesson(courseIdNumber, editingLesson.id, lessonData);
      } else {
        await createLesson(courseIdNumber, lessonData);
      }
      setModalOpen(false);
      setEditingLesson(null);
      setStorageFiles([]);
      await loadLessons();
      toast.show({
        severity: "success",
        summary: "Готово",
        detail: editingLesson ? "Материал обновлён" : "Материал добавлен",
        life: 2500,
      });
    } catch (error) {
      const serverErrors = getServerErrorData(error);
      if (serverErrors) {
        setExternalErrors(formatServerErrors(serverErrors));
      } else {
        logger.error("Ошибка сохранения материала Базы знаний:", error);
        toast.show({ severity: "error", summary: "Ошибка", detail: "Не удалось сохранить материал", life: 3000 });
      }
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deletingLesson) return;
    try {
      await deleteLesson(courseIdNumber, deletingLesson.id);
      setDeletingLesson(null);
      await loadLessons();
      toast.show({ severity: "success", summary: "Удалено", detail: "Материал удалён", life: 2500 });
    } catch (error) {
      logger.error("Ошибка удаления материала Базы знаний:", error);
      toast.show({ severity: "error", summary: "Ошибка", detail: "Не удалось удалить материал", life: 3000 });
    }
  };

  const headers = [
    { label: "№", key: "order", fieldName: "order", width: "10%" },
    { label: "Название", key: "title", fieldName: "title", width: "42%" },
    { label: "Раздел", key: "category", fieldName: "category", width: "24%" },
    { label: "Материалы", key: "materials", sortable: false, width: "14%" },
    { label: "", key: "buttons", sortable: false, width: "10%" },
  ];

  const rows = lessons.map((lesson) => {
    const materialCount = (lesson.files_from_storage || []).length + (lesson.video_url ? 1 : 0);
    return {
      order: <TableText>{lesson.order}</TableText>,
      title: <TableText weight={500}>{lesson.title}</TableText>,
      category: <TableText>{lesson.category_title || "—"}</TableText>,
      materials: <TableText>{materialCount || "—"}</TableText>,
      buttons: course?.can_manage ? (
        <ButtonsContainer>
          <IconButton icon={editIcon} altText="Редактировать" onClick={() => openEdit(lesson)} />
          <IconButton icon={delIcon} tone="danger" altText="Удалить" onClick={() => setDeletingLesson(lesson)} />
        </ButtonsContainer>
      ) : null,
    };
  });

  return (
    <LessonsPageContainer>
      <Sidebar />
      <ContentWrapper>
        <HeaderContainer>
          <div>
            <Title>Материалы Базы знаний</Title>
            {course ? <TableText>{course.title}</TableText> : null}
          </div>
          <ButtonWrapper>
            {course ? <CompactLink to={`/knowledge-base/${course.id}`}>Просмотр</CompactLink> : null}
            {course?.can_manage ? (
              <Button type="button" variant="primary" size="lg" onClick={openCreate}>
                Новый материал
              </Button>
            ) : null}
          </ButtonWrapper>
        </HeaderContainer>

        <Table
          emptyState={{
            title: "Материалов пока нет",
            description: "Добавьте первый материал Базы знаний.",
          }}
          headers={headers}
          rows={rows}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          onSort={handleSort}
          rowAmountOptions={[
            { label: "6 материалов", value: 6 },
            { label: "12 материалов", value: 12 },
            { label: "18 материалов", value: 18 },
          ]}
          specialRowKey="title"
          loading={loading}
        />

        {modalOpen ? (
          <CreateModal
            title={editingLesson ? "Редактировать материал" : "Новый материал"}
            hasDragAndDrop={true}
            fields={[
              { name: "order", type: "number", placeholder: "Порядковый номер", required: true },
              { name: "title", type: "text", placeholder: "Название", required: true },
              {
                name: "category",
                type: "selectWithInput",
                placeholder: "Раздел",
                options: categories,
                required: true,
              },
              { name: "content", type: "textarea", placeholder: "Содержание" },
            ]}
            onSubmit={handleSave}
            submitButtonText={editingLesson ? "Сохранить" : "Создать"}
            onCancel={() => {
              setModalOpen(false);
              setEditingLesson(null);
              setStorageFiles([]);
            }}
            onRemoveFile={(fileId) => setStorageFiles((items) => items.filter((file) => file.id !== fileId))}
            initialValues={
              editingLesson
                ? {
                    order: editingLesson.order.toString(),
                    title: editingLesson.title,
                    category: editingLesson.category_title || "",
                    content: editingLesson.content || "",
                    video_url: editingLesson.video_url || "",
                  }
                : {}
            }
            files_from_storage={editingLesson ? storageFiles : []}
            externalErrors={externalErrors}
            isLoading={saving}
          />
        ) : null}

        <ConfirmModal
          isOpen={Boolean(deletingLesson)}
          title="Удаление материала"
          message={`Вы уверены, что хотите удалить материал «${deletingLesson?.title || ""}»?`}
          onConfirm={() => void confirmDelete()}
          onCancel={() => setDeletingLesson(null)}
        />
      </ContentWrapper>
    </LessonsPageContainer>
  );
};

export default KnowledgeBaseManagePage;
